import "./css/index.css";
import React, { forwardRef } from "react";
import { ODSLinkProps } from "./ODSLink.types";
import classNames from "classnames";
import { ODSText } from "../ODSText";
import { ODSIconName } from "../ODSIcons/ODSIcon.types";
import ODSIcon from "../ODSIcons/ODSIcon";

const ODSLink = forwardRef<HTMLAnchorElement, ODSLinkProps>(
  (
    {
      Component = "a",
      variant = "primary",
      type = "primary",
      disabled = false,
      label,
      tabIndex = 0,
      children,
      href,
      leftIcon,
      rightIcon,
      className,
      alignment = "left",
      linkType = "standard",
      onClick,
      ariaLabelLeftIcon,
      ariaLabelRightIcon,
      ...props
    },
    ref
  ) => {
    const isODSIconName = (
      icon: React.ReactNode | ODSIconName
    ): icon is ODSIconName => {
      return typeof icon === "string";
    };

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (disabled) {
        event.preventDefault();
        return;
      }
      if (linkType === "button" && onClick) {
        event.preventDefault();
        onClick(event);
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
      if (
        linkType === "button" &&
        (event.key === "Enter" || event.key === " ")
      ) {
        event.preventDefault();
        onClick?.(event as unknown as React.MouseEvent<HTMLAnchorElement>);
      }
    };

    const normalizedProps =
      linkType === "button"
        ? {
            role: "button",
            onClick: handleClick,
            onKeyDown: handleKeyDown,
            href: "#",
          }
        : {
            href: !disabled ? href : "#",
          };

    return (
      <Component
        ref={ref}
        className={classNames(
          `ODSLink`,
          `ODSLink--disabled-${disabled}`,
          `ODSLink--type-${type ?? variant}`,
          `ODSLink--alignment-${alignment}`,
          `ODSLink--leftIcon-${!!leftIcon}`,
          `ODSLink--rightIcon-${!!rightIcon}`,
          className
        )}
        aria-label={label}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : tabIndex}
        {...props}
        {...normalizedProps}
      >
        <span className="ODSLink-LinkContainer">
          {isODSIconName(leftIcon) ? (
            <span
              className="ODSLink-LeftIcon"
              aria-hidden={!ariaLabelLeftIcon ? "true" : "false"}
            >
              <ODSIcon
                name={leftIcon}
                decorative
                size={16}
                aria-label={ariaLabelLeftIcon || undefined}
              />
            </span>
          ) : (
            leftIcon && <span className="ODSLink-LeftIcon">{leftIcon}</span>
          )}
          {label ? (
            <ODSText as="span" className={"ODSLink-Link"}>
              {label}
            </ODSText>
          ) : (
            children
          )}
          {isODSIconName(rightIcon) ? (
            <span
              className="ODSLink-RightIcon"
              aria-hidden={!ariaLabelRightIcon ? "true" : "false"}
            >
              <ODSIcon
                name={rightIcon}
                decorative
                size={16}
                aria-label={ariaLabelRightIcon || undefined}
              />
            </span>
          ) : (
            rightIcon && <span className="ODSLink-RightIcon">{rightIcon}</span>
          )}
        </span>
      </Component>
    );
  }
);

ODSLink.displayName = "ODSLink";

export default ODSLink;
